import { Trans } from '@lingui/react/macro';

interface ReviewsProps {
  /** Render a compact layout suitable for sidebars (e.g. sign-in/sign-up pages) */
  compact?: boolean;
}

export const reviews = [
  {
    text: (
      <Trans>
        "I didn't realize how much I was rambling until I saw myself answering questions on camera. Pavone helped me <strong className="font-semibold text-gray-900">cut my answers in half</strong> and sound way more confident. I <strong className="font-semibold text-gray-900">passed to the final round</strong> after practicing for a week."
      </Trans>
    ),
    name: 'Sarah M.',
    role: <Trans>Product Manager</Trans>,
    image: '/images/reviews/review-2-f.jpg',
  },
  {
    text: (
      <Trans>
        "I'm a senior dev but interviews always made me nervous. Practicing answers out loud with feedback made a <strong className="font-semibold text-gray-900">huge difference</strong>. I finally felt like I was <strong className="font-semibold text-gray-900">explaining things clearly</strong> instead of improvising under pressure."
      </Trans>
    ),
    name: 'Michael T.',
    role: <Trans>Software Engineer</Trans>,
    image: '/images/reviews/review-1-m.webp',
  },
  {
    text: (
      <Trans>
        "English isn't my first language and I always felt that hurt me in interviews. Pavone helped me <strong className="font-semibold text-gray-900">slow down, structure my answers</strong>, and sound more confident. I got <strong className="font-semibold text-gray-900">much better feedback</strong> from interviewers after that."
      </Trans>
    ),
    name: 'Emily R.',
    role: <Trans>Data Analyst</Trans>,
    image: '/images/reviews/review-4-f.png',
  },
  {
    text: (
      <Trans>
        "What I liked most is that it feels <strong className="font-semibold text-gray-900">private</strong>. I could practice embarrassing answers without anyone watching. Seeing the <strong className="font-semibold text-gray-900">progress between my first and last attempts</strong> was honestly motivating."
      </Trans>
    ),
    name: 'David L.',
    role: <Trans>Marketing Manager</Trans>,
    image: '/images/reviews/review-3-m.jpg',
  },
  {
    text: (
      <Trans>
        "I thought my answers were fine until Pavone pointed out <strong className="font-semibold text-gray-900">filler words and lack of structure</strong>. Small changes, but they made me <strong className="font-semibold text-gray-900">sound way more senior</strong>. Worth it just for that."
      </Trans>
    ),
    name: 'Lisa K.',
    role: <Trans>Team Lead</Trans>,
    image: '/images/reviews/review-5-f.png',
  },
  {
    text: (
      <Trans>
        "I used Pavone for about two weeks before interviews. By the time the real calls happened, answering questions felt <strong className="font-semibold text-gray-900">familiar instead of stressful</strong>. I ended up <strong className="font-semibold text-gray-900">getting an offer</strong> I was really hoping for."
      </Trans>
    ),
    name: 'James P.',
    role: <Trans>Business Analyst</Trans>,
    image: '/images/reviews/review-6-m.png',
  },
  {
    text: (
      <Trans>
        "Recording myself was uncomfortable at first, but that's exactly why it worked. Pavone forced me to <strong className="font-semibold text-gray-900">face how I actually come across</strong> and improve it. Super useful if you <strong className="font-semibold text-gray-900">struggle with confidence</strong> on camera."
      </Trans>
    ),
    name: 'Anna W.',
    role: <Trans>UX Designer</Trans>,
    image: '/images/reviews/review-7-f.png',
  },
  {
    text: (
      <Trans>
        "I've done mock interviews with friends before, but this was <strong className="font-semibold text-gray-900">easier to fit into my schedule</strong>. Short practice sessions, clear feedback, no pressure. I'd <strong className="font-semibold text-gray-900">definitely use it again</strong> before my next job search."
      </Trans>
    ),
    name: 'Robert C.',
    role: <Trans>Operations Manager</Trans>,
    image: '/images/reviews/review-8-m.png',
  },
];

function ReviewCard({ review, textSize = 'text-base' }: { review: typeof reviews[number]; textSize?: string }) {
  return (
    <div className="p-4 sm:p-6 flex flex-col h-full">
      <p className={`text-gray-700 ${textSize} mb-4 leading-relaxed flex-1`}>
        {review.text}
      </p>
      <div className="flex items-center gap-3 mt-auto">
        <img
          src={review.image}
          alt={review.name}
          className="w-8 h-8 rounded-full border-2 border-white object-cover"
        />
        <div>
          <p className="text-gray-900 font-medium text-sm">{review.name}</p>
          <p className="text-gray-500 text-xs">{review.role}</p>
        </div>
      </div>
    </div>
  );
}

function CompactReviewCard({ review }: { review: typeof reviews[number] }) {
  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-lg">
      <p className="text-gray-700 text-sm mb-4 leading-relaxed">
        {review.text}
      </p>
      <div className="flex items-center gap-3">
        <img
          src={review.image}
          alt={review.name}
          className="w-8 h-8 rounded-full border-2 border-white object-cover"
        />
        <div>
          <p className="text-gray-900 font-medium text-xs">{review.name}</p>
          <p className="text-gray-500 text-xs">{review.role}</p>
        </div>
      </div>
    </div>
  );
}

/** Compact two-column layout for sign-in / sign-up sidebars */
export function CompactReviews() {
  return (
    <>
      <div className="w-1/4 px-2">
        <div className="space-y-4">
          {reviews.slice(0, 3).map((review, i) => (
            <CompactReviewCard key={i} review={review} />
          ))}
        </div>
      </div>
      <div className="w-1/4 px-2">
        <div className="space-y-4">
          {reviews.slice(3, 6).map((review, i) => (
            <CompactReviewCard key={i} review={review} />
          ))}
        </div>
      </div>
    </>
  );
}

/** Full-width reviews section for landing / pricing pages */
export default function Reviews({ compact }: ReviewsProps) {
  if (compact) {
    return <CompactReviews />;
  }

  return (
    <section id="reviews" className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1080px] mx-auto w-full px-4">
        <div className="text-center mb-8 sm:mb-12">
          <p className="text-sm font-medium text-gray-600 mb-4">
            <Trans>Trusted by professionals worldwide</Trans>
          </p>
          <div className="flex items-center justify-center mb-6 sm:mb-8">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              ))}
            </div>
          </div>
        </div>

        {/* Reviews Grid - 3 on mobile, 4 on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {reviews.slice(0, 3).map((review, i) => (
            <ReviewCard key={i} review={review} />
          ))}
          {/* 4th review - Hidden on mobile, shown on lg+ */}
          <div className="hidden lg:flex flex-col">
            <ReviewCard review={reviews[3]} />
          </div>
        </div>

        {/* Second row - Hidden on mobile, shown on lg+ */}
        <div className="hidden lg:grid grid-cols-4 gap-8 mt-8">
          {reviews.slice(4, 8).map((review, i) => (
            <ReviewCard key={i} review={review} />
          ))}
        </div>
      </div>
    </section>
  );
}
